import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shared/ui/primitives';
import { Settings, Monitor, Server, Shield } from 'lucide-react';
import { config, getApiEndpoint } from '@shared/config';
import { getServiceLabelColorClass } from '@/utils/serviceLabelColors';

const LED_COLORS = {
  healthy: 'bg-green-500',
  unhealthy: 'bg-red-500',
  unknown: 'bg-gray-400',
};

export type HealthStatus = 'healthy' | 'unhealthy' | 'unknown';

export interface ServiceHealth {
  frontend?: HealthStatus;
  frontendAdmin?: HealthStatus;
  backend?: HealthStatus;
}

interface ComponentHealth {
  exists: boolean;
  status: HealthStatus;
  lastCheck?: string;
}

export interface ServiceCardData {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  iconName?: string;
  href: string;
  labelType: string[];
  adminHref?: string;
  hasAccess?: boolean;
  hasAdminAccess?: boolean;
  isLoading?: boolean;
  health?: ServiceHealth;
  isServiceFolder?: boolean; // services 폴더 안의 서비스인지 여부
}

export interface ServiceCardProps extends ServiceCardData {
  onClick: () => void;
  onAdminClick?: (e: React.MouseEvent) => void;
}

export function ServiceCard({
  id,
  label,
  description,
  icon,
  href,
  labelType,
  hasAccess = true,
  hasAdminAccess = true,
  isLoading = false,
  adminHref,
  health,
  isServiceFolder = false,
  onClick,
  onAdminClick,
}: ServiceCardProps) {
  const isInactive = hasAccess === false;
  const isAdminCard =
    id === 'admin' ||
    href === '/admin' ||
    ['Admin', 'Portal 관리자', 'Portal System Admin'].includes(label);
  const showAdmin = !!adminHref && !isAdminCard;

  // 기본 portal 서비스 목록 (LED 1개만 표시)
  const PORTAL_CORE_SERVICES = ['it-service-request', 'tenant-admin', 'admin'];
  
  // services 폴더 안의 서비스인지 확인 (3개 LED 표시 여부)
  // 조건:
  // 1. labelType에 'Service'가 포함되어야 함
  // 2. labelType에 'System'이 없어야 함
  // 3. id가 기본 portal 서비스가 아니어야 함
  const hasServiceLabel = Array.isArray(labelType) && labelType.includes('Service');
  const hasSystemLabel = Array.isArray(labelType) && labelType.includes('System');
  const isPortalCoreService = PORTAL_CORE_SERVICES.includes(id);
  // localhost(dev): services 폴더 서비스 → backend/frontend/frontend-admin 3개 LED + health check
  // itops-portal.iampam.io(prod): LED 1개
  const isServicesFolderService =
    isServiceFolder || (hasServiceLabel && !hasSystemLabel && !isPortalCoreService);
  const shouldShowThreeLEDs = config.isDev && isServicesFolderService;

  // Health check 상태 관리 (services 폴더 안의 서비스만)
  const [componentHealth, setComponentHealth] = useState<{
    backend: ComponentHealth;
    frontend: ComponentHealth;
    frontendAdmin: ComponentHealth;
  }>({
    backend: { exists: false, status: 'unknown' },
    frontend: { exists: false, status: 'unknown' },
    frontendAdmin: { exists: false, status: 'unknown' },
  });

  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Health check 함수
  const checkServiceHealth = useCallback(async () => {
    if (!shouldShowThreeLEDs) return;

    try {
      const response = await fetch(getApiEndpoint(`/services/${encodeURIComponent(id)}/health`));
      if (!response.ok) {
        // Health check 실패 시 모든 컴포넌트를 unknown으로 설정
        setComponentHealth({
          backend: { exists: false, status: 'unknown' },
          frontend: { exists: false, status: 'unknown' },
          frontendAdmin: { exists: false, status: 'unknown' },
        });
        // 실패 시 30초 후 재시도
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = setTimeout(() => {
          checkServiceHealth();
        }, 30000);
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Expected JSON, got ${contentType || 'unknown content-type'}`);
      }

      const data = await response.json();
      const checks = data.checks || {};

      const newHealth: {
        backend: ComponentHealth;
        frontend: ComponentHealth;
        frontendAdmin: ComponentHealth;
      } = {
        backend: {
          exists: checks.backend?.exists || false,
          status: (checks.backend?.status === 'ok' ? 'healthy' :
            checks.backend?.status === 'down' ? 'unhealthy' : 'unknown') as HealthStatus,
          lastCheck: new Date().toISOString(),
        },
        frontend: {
          exists: checks.frontend?.exists || false,
          status: (checks.frontend?.status === 'ok' ? 'healthy' :
            checks.frontend?.status === 'down' ? 'unhealthy' : 'unknown') as HealthStatus,
          lastCheck: new Date().toISOString(),
        },
        frontendAdmin: {
          exists: checks['frontend-admin']?.exists || false,
          status: (checks['frontend-admin']?.status === 'ok' ? 'healthy' :
            checks['frontend-admin']?.status === 'down' ? 'unhealthy' : 'unknown') as HealthStatus,
          lastCheck: new Date().toISOString(),
        },
      };

      setComponentHealth(newHealth);

      // 실패한 컴포넌트가 있으면 30초 후 재시도
      const hasFailure = Object.values(newHealth).some(
        h => h.exists && h.status === 'unhealthy'
      );

      if (hasFailure) {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = setTimeout(() => {
          checkServiceHealth();
        }, 30000); // 30초
      } else {
        // 모든 컴포넌트가 healthy이면 재시도 타이머 정리
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }
      }
    } catch (error) {
      console.error(`Health check failed for ${id}:`, error);
      setComponentHealth({
        backend: { exists: false, status: 'unknown' },
        frontend: { exists: false, status: 'unknown' },
        frontendAdmin: { exists: false, status: 'unknown' },
      });
      // 에러 발생 시에도 30초 후 재시도
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        checkServiceHealth();
      }, 30000);
    }
  }, [id, shouldShowThreeLEDs]);

  // Health check 초기화 및 주기적 체크
  useEffect(() => {
    if (!shouldShowThreeLEDs) return;

    // 초기 체크
    checkServiceHealth();

    // 5분마다 재체크 (성공한 경우)
    healthCheckIntervalRef.current = setInterval(() => {
      checkServiceHealth();
    }, 300000); // 5분

    return () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [id, shouldShowThreeLEDs, checkServiceHealth]);

  return (
    <Card
      className={`p-4 relative group overflow-hidden border-2
        ${isInactive 
          ? 'opacity-50 grayscale cursor-not-allowed bg-muted/30' 
          : hasAccess && !isLoading
            ? 'cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary/50'
            : 'cursor-wait opacity-75'
        }
        ${hasAccess && !isLoading ? 'before:absolute before:inset-0 before:bg-gradient-to-br before:from-primary/0 before:via-primary/0 before:to-primary/0 hover:before:from-primary/5 hover:before:via-primary/3 hover:before:to-primary/0 before:transition-all before:duration-300' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex flex-col items-center text-center space-y-3">
        {/* Label & Admin Icon */}
        <div className="w-full flex items-start justify-between">
          <div className="flex gap-1 flex-wrap">
            {labelType.map((type) => (
              <div
                key={type}
                className={`px-2 py-1 rounded-full font-semibold text-[0.65rem] ${getServiceLabelColorClass(type)}`}
              >
                {type}
              </div>
            ))}
          </div>
          {showAdmin && onAdminClick && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onAdminClick}
                    className={`
                      relative z-10 p-1.5 rounded-full border border-transparent
                      transition-all duration-300 ease-out group/gear
                      focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                      ${hasAdminAccess === false
                        ? 'opacity-40 grayscale cursor-not-allowed text-muted-foreground/50'
                        : 'text-muted-foreground hover:text-primary hover:bg-primary/15 hover:shadow-lg hover:shadow-primary/30 hover:border-primary/30 active:scale-95'
                      }
                    `}
                    aria-label="관리자 설정"
                    disabled={hasAdminAccess === false}
                  >
                    <Settings className={`
                      relative z-10 w-5 h-5 transition-all duration-300 ease-out
                      ${hasAdminAccess === false ? '' : 'group-hover/gear:rotate-90 group-hover/gear:scale-125 group-hover/gear:drop-shadow-lg'}
                    `} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>관리자 설정</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Icon */}
        <div className="relative flex items-center justify-center h-16 mb-1 group/icon">
          {hasAccess && !isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 via-primary/10 to-transparent group-hover:from-primary/30 group-hover:via-primary/20 blur-sm group-hover:blur-md transition-all duration-300" />
            </div>
          )}
          <div className={`relative z-10 transition-all duration-300 ${hasAccess && !isLoading ? 'group-hover:scale-110 group-hover:drop-shadow-2xl' : ''}`}>
            {icon}
          </div>
        </div>

        {/* Label */}
        <div className="font-semibold text-base text-foreground">{label}</div>

        {/* Description */}
        <div className={`text-xs ${isInactive ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
          {description}
        </div>
      </div>

      {/* Health Status LED Indicators */}
      <div className="absolute bottom-3 right-3 z-10">
        {shouldShowThreeLEDs ? (
          // services 폴더 안의 서비스: 3개 LED (backend, frontend, frontend-admin)
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${LED_COLORS[componentHealth.backend.status]} shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)] cursor-pointer hover:scale-110 transition-transform`}
                    title="Backend"
                    role="button"
                    tabIndex={0}
                  />
                  </TooltipTrigger>
                <TooltipContent side="left">
                  <p className="text-xs font-medium">Backend</p>
                </TooltipContent>
                </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${LED_COLORS[componentHealth.frontend.status]} shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)] cursor-pointer hover:scale-110 transition-transform`}
                    title="Frontend"
                    role="button"
                    tabIndex={0}
                  />
                    </TooltipTrigger>
                <TooltipContent side="left">
                  <p className="text-xs font-medium">Frontend</p>
                </TooltipContent>
                  </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${LED_COLORS[componentHealth.frontendAdmin.status]} shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)] cursor-pointer hover:scale-110 transition-transform`}
                    title="Admin"
                    role="button"
                    tabIndex={0}
                  />
                  </TooltipTrigger>
                <TooltipContent side="left">
                  <p className="text-xs font-medium">Admin</p>
                </TooltipContent>
                </Tooltip>
            </div>
          </TooltipProvider>
        ) : (
          // prod(itops-portal.iampam.io) 또는 portal core/system 서비스: LED 1개
          <div className={`w-1.5 h-1.5 rounded-full ${LED_COLORS.healthy} shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)]`} />
        )}
        </div>
    </Card>
  );
}
